// Local Imports
import { Bbox, Point } from "GenAlphaModel";
import { PageBbox } from "./page_bbox";

// # World Transform
//
// A simplified subset in which there is no rotation, only scaling (uniform in the two axes) and translation.
// The y-axis inversion between schematic and page coordinates is handled here.
// Applying a transform {scale, offset} to a point {x,y} produces a new point at {x*scale+offset.x, -y*scale+offset.y}.
//
export interface WorldTransform {
  scale: number;
  offset: Point;
}
export const WorldTransform = {
  identity: (): WorldTransform => {
    return { scale: 1, offset: Point.origin() };
  },

  // Create a new transform that is the inverse of `t`.
  inverse: (t: WorldTransform): WorldTransform => {
    const { scale, offset } = t;
    return {
      scale: 1 / scale,
      offset: {
        // This part is the trick:
        x: -offset.x / scale,
        y: offset.y / scale,
      },
    };
  },

  // Apply to a `Point`.
  applyToPoint: (t: WorldTransform, p: Point): Point => {
    const { scale, offset } = t;
    // Note y-axis inversion is applied right here!
    return Point.new(p.x * scale + offset.x, -p.y * scale + offset.y);
  },
};

// # WorldTransforms
// Pair of inverse coordinate transforms between schematic and page coordinates.
export class WorldTransforms {
  private constructor(
    public schToPage: WorldTransform,
    public pageToSch: WorldTransform
  ) {}

  // Default transforms are identity.
  static default() {
    return new WorldTransforms(
      WorldTransform.identity(),
      WorldTransform.identity()
    );
  }

  // Create from an `schToPage` coordinate transform.
  static fromSchToPage = (schToPage: WorldTransform): WorldTransforms => {
    return new WorldTransforms(schToPage, WorldTransform.inverse(schToPage));
  };

  // Create from a `pageToSch` coordinate transform.
  static fromPageToSch = (pageToSch: WorldTransform): WorldTransforms => {
    return new WorldTransforms(WorldTransform.inverse(pageToSch), pageToSch);
  };

  // Convert `sch` coordinates to `page` coordinates.
  toPage = (sch: Point): Point => {
    return WorldTransform.applyToPoint(this.schToPage, sch);
  };

  // Convert `page` coordinates to `sch` coordinates.
  toSch = (page: Point): Point => {
    return WorldTransform.applyToPoint(this.pageToSch, page);
  };

  // Convert a page bbox to a schematic bbox.
  toSchBbox = (pageBbox: PageBbox): Bbox => {
    const { pageToSch } = this;
    const pagemin = Point.new(pageBbox.left, pageBbox.top);
    const pagemax = Point.new(pageBbox.right, pageBbox.bottom);
    const min = WorldTransform.applyToPoint(pageToSch, pagemin);
    const max = WorldTransform.applyToPoint(pageToSch, pagemax);
    return Bbox.fromPoints(min, max);
  };

  // Update our transforms-pair from either a schematic-to-page or page-to-schematic transform.
  update = (xform: WorldTransform, kind: "schToPage" | "pageToSch") => {
    if (kind == "schToPage") {
      this.schToPage = xform;
      this.pageToSch = WorldTransform.inverse(xform);
    } else {
      this.pageToSch = xform;
      this.schToPage = WorldTransform.inverse(xform);
    }
  };
}
